import { useEffect, useMemo, useState } from "react"
import {
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap"
import { MathJax } from "better-react-mathjax"
import { BMD_COPY } from "../constants/bmd.constants"
import {
  getBmdAssignedActivityPreview,
  getBmdPreviewQuestions,
} from "../services/bmd.service"
import type {
  BmdPreviewAnswer,
  BmdPreviewQuestion,
} from "../types/bmd.types"

/**
 * Two sources, one renderer.
 *
 * At ASSIGN time there is no test yet, so the preview is built from a year and
 * a set of topics. Once a test EXISTS, that no longer describes it: the content
 * may have changed since, and a prerequisite test has no year or topics at all,
 * being built from skills. So an assigned test is previewed from its own
 * question rows instead. The server returns the same shape for both, which is
 * why one modal can serve both.
 */
type BmdPreviewModalProps = {
  isOpen: boolean
  onClose: () => void
  yearLevelLabel: string
  /** Assign-time source. Ignored when `activityKey` is set. */
  yearLevelId?: number
  subjectId?: number
  topicIds?: number[]
  /** Assigned-test source: preview what this activity actually holds. */
  activityKey?: string | null
  studentKey?: string | null
}

export function BmdPreviewModal({
  isOpen,
  onClose,
  yearLevelId,
  yearLevelLabel,
  subjectId,
  topicIds,
  activityKey,
  studentKey,
}: BmdPreviewModalProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [questions, setQuestions] = useState<BmdPreviewQuestion[]>([])
  const [questionCount, setQuestionCount] = useState<number>(0)
  const [activeIndex, setActiveIndex] = useState<number>(0)

  const [isPrereqTest, setIsPrereqTest] = useState<boolean>(false)

  useEffect(() => {
    if (!isOpen) return
    // An assigned activity needs no year or subject; those belong to the
    // assign-time path only.
    if (!activityKey && (!yearLevelId || !subjectId)) return

    const controller = new AbortController()
    setIsLoading(true)
    setError(null)
    setActiveIndex(0)
    setIsPrereqTest(false)

    const request = activityKey
      ? getBmdAssignedActivityPreview(
          { pk_activity_key: activityKey, pk_student_key: studentKey || undefined },
          controller.signal,
        ).then((res) => {
          setIsPrereqTest(!!res.is_prereq_test)
          return res
        })
      : getBmdPreviewQuestions(
          {
            year_level_id: yearLevelId as number,
            subject_id: subjectId as number,
            topic_ids: topicIds || [],
          },
          controller.signal,
        )

    request
      .then((res) => {
        setQuestions(res.questions)
        setQuestionCount(res.question_count)
        setIsLoading(false)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setError(err?.message || "Failed to load preview")
        setQuestions([])
        setQuestionCount(0)
        setIsLoading(false)
      })

    return () => {
      controller.abort()
    }
  }, [isOpen, yearLevelId, subjectId, topicIds, activityKey, studentKey])

  const activeQuestion = useMemo<BmdPreviewQuestion | null>(
    () => questions[activeIndex] || null,
    [questions, activeIndex],
  )

  const goPrevious = () => setActiveIndex((prev) => Math.max(prev - 1, 0))
  const goNext = () =>
    setActiveIndex((prev) => Math.min(prev + 1, questions.length - 1))

  const handleJump = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = parseInt(event.target.value, 10)
    if (!Number.isNaN(next)) setActiveIndex(next)
  }

  const renderHtml = (html?: string) => ({ __html: html || "" })

  const renderAnswer = (answer: BmdPreviewAnswer, answerIndex: number) => {
    const isCorrect = Number(answer.answer_is_correct) === 1
    const optionLabel = String.fromCharCode(65 + answerIndex)
    return (
      <li
        key={answer.answer_id}
        className={`p-3 mb-3 ${
          isCorrect ? "correct-ans" : "bg-white border rounded"
        }`}
      >
        <div className="d-flex justify-content-between align-items-start gap-3">
          <div className="d-flex align-items-start gap-3 flex-grow-1">
            <div
              className={`fw-800 font-size-16 ${
                isCorrect ? "text-success" : "text-dark"
              }`}
              style={{ minWidth: 28 }}
            >
              {optionLabel}.
            </div>
            <div
              className="question_container flex-grow-1"
              dangerouslySetInnerHTML={renderHtml(answer.answer_text)}
            />
          </div>
          {isCorrect ? (
            <span className="badge bg-success text-white">Correct</span>
          ) : null}
        </div>
      </li>
    )
  }

  // An assigned test is described by how many questions it holds. "3 topics
  // selected" is an assign-time idea and means nothing once the test exists -
  // and a prerequisite test has no topics to count at all.
  const selectedTopics = topicIds || []
  const subtitle = activityKey
    ? `${questionCount} question${questionCount === 1 ? "" : "s"}`
    : selectedTopics.length > 0
      ? `${selectedTopics.length} topic${selectedTopics.length === 1 ? "" : "s"} selected`
      : "All topics"

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="xl"
      scrollable
      centered
      className="remove-bd preview-activity"
    >
      <ModalHeader toggle={onClose} className="modal-header--background border-0">
          <div className="w-100 pe-4">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mb-1 text-white fw-800">
                  {BMD_COPY.PREVIEW_MODAL_TITLE}
                </h5>
                <div className="luminous-wht font-size-13">
                  {isPrereqTest
                    ? "Prerequisite Test"
                    : yearLevelLabel || "Diagnostics"}{" "}
                  · {subtitle}
                </div>
              </div>
              {!isLoading && questionCount > 0 ? (
                <div className="text-md-end">
                  <div className="text-yellow fw-800 font-size-20">
                    {questionCount}
                  </div>
                  <div className="luminous-wht font-size-12">Questions</div>
                </div>
              ) : null}
            </div>
          </div>
        </ModalHeader>

      {/* MathJax must NOT wrap the header/body/footer as a group: reactstrap's
          `scrollable` needs those three to be direct children of
          .modal-content, and an element in between collapses the flex layout —
          the body stops scrolling and Previous/Close/Next end up below the
          viewport, which is exactly what the PM reported (2026-08-07). */}
      <ModalBody className="bg-supermild">
        <MathJax inline dynamic>
          <div className="assessment-width-hold">
            {isLoading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <div className="mt-3 text-muted">{BMD_COPY.PREVIEW_LOADING}</div>
              </div>
            ) : null}

            {error && !isLoading ? (
              <div className="card shadow-sm border-0">
                <div className="card-body p-4 text-danger">{error}</div>
              </div>
            ) : null}

            {!isLoading && !error && questions.length > 0 ? (
              <div className="assessement-section">
                <div className="card shadow-sm border-0 mb-4">
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-end flex-wrap gap-3">
                      <div>
                        <Label
                          for="bmd-preview-question-select"
                          className="text-dark fw-700 mb-2"
                        >
                          Jump to question
                        </Label>
                        <Input
                          id="bmd-preview-question-select"
                          type="select"
                          value={String(activeIndex)}
                          onChange={handleJump}
                          style={{ minWidth: "260px" }}
                        >
                          {questions.map((q, index) => (
                            <option key={q.diagnostic_id} value={String(index)}>
                              Question {index + 1}: {q.display_name}
                            </option>
                          ))}
                        </Input>
                      </div>
                      <div className="text-md-end">
                        <div className="fw-700 text-dark">
                          {activeIndex + 1} of {questions.length}
                        </div>
                        {activeQuestion ? (
                          <div className="text-muted font-size-13">
                            Topic: {activeQuestion.display_name}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                {activeQuestion ? (
                  <div className="card shadow-sm border-0">
                    <div className="card-body p-4">
                      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                        <div>
                          <h5 className="text-dark fw-800 mb-1">
                            Question {activeIndex + 1}
                          </h5>
                          <p className="mb-0 text-muted">
                            {activeQuestion.display_name}
                          </p>
                        </div>
                        <div className="text-md-end">
                          <div className="fw-700 text-dark">
                            {activeIndex + 1} of {questions.length}
                          </div>
                        </div>
                      </div>

                      <div className="question_container mb-4">
                        <div
                          dangerouslySetInnerHTML={renderHtml(
                            activeQuestion.diagnostic_text,
                          )}
                        />
                      </div>

                      <div>
                        <h6 className="text-dark fw-700 mb-3">Answers</h6>
                        {activeQuestion.answers?.length ? (
                          <ul className="list-unstyled mb-0">
                            {activeQuestion.answers.map(renderAnswer)}
                          </ul>
                        ) : (
                          <div className="text-muted">
                            No answers available for this question.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {!isLoading && !error && questions.length === 0 ? (
              <div className="card shadow-sm border-0">
                <div className="card-body p-4 text-muted">
                  {BMD_COPY.PREVIEW_EMPTY}
                </div>
              </div>
            ) : null}
          </div>
        </MathJax>
      </ModalBody>

      <ModalFooter className="border-0 bg-supermild pt-0">
          <div className="assessment-width-hold w-100 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <button
              type="button"
              className="btn-cst-secondary-md"
              onClick={goPrevious}
              disabled={activeIndex === 0 || questions.length === 0}
            >
              Previous
            </button>
            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                className="btn-cst-secondary-md"
                onClick={onClose}
              >
                Close
              </button>
              <button
                type="button"
                className="btn-cst-danger-md"
                onClick={goNext}
                disabled={
                  questions.length === 0 || activeIndex === questions.length - 1
                }
              >
                Next
              </button>
            </div>
          </div>
      </ModalFooter>
    </Modal>
  )
}

export default BmdPreviewModal
